import React, { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Sign in.
 *
 * MOBILE TAP RELIABILITY (HeyCatch recorded repeated taps on this button)
 *   * The submit control is a real <button type="submit"> inside the form, so a
 *     tap, the Enter key and the on-screen keyboard's "Go" all take the same
 *     path - and nothing is positioned over it.
 *   * It is 48px tall with `touch-manipulation`, which removes the mobile
 *     double-tap-zoom delay that makes a first tap feel ignored.
 *   * It reports work immediately: the label changes to "Signing you in...",
 *     a spinner appears and `aria-busy` is set, so there is visible proof the
 *     tap registered even on a slow connection.
 *   * Duplicate submissions are blocked twice over - `busy` disables the
 *     button, and a ref guard rejects a second submit in the same tick (which
 *     is how a rapid double-tap used to get through before React re-rendered).
 *   * Errors stay human. Backend messages are already plain English; anything
 *     unexpected (5xx, timeout, parse failure) is replaced with a friendly
 *     line instead of leaking status codes or stack detail.
 */
const GENERIC_FAILURE = "We couldn't sign you in just now. Please try again in a moment.";

function friendlyLoginError(err) {
  const status = err?.response?.status;
  // 401/403 carry deliberate, member-safe wording from the API ("Incorrect
  // email or password.", "This account has been banned..."). Anything else is
  // infrastructure and must not be shown verbatim.
  if (status === 401 || status === 403 || status === 429) {
    return apiError(err, GENERIC_FAILURE);
  }
  if (!status) {
    return "We can't reach Sponsor Me right now. Check your connection and try again.";
  }
  return GENERIC_FAILURE;
}

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);

  React.useEffect(() => {
    if (user) navigate(params.get("next") || "/dashboard", { replace: true });
  }, [user, navigate, params]);

  const submit = async (e) => {
    e.preventDefault();
    // Synchronous guard: a double-tap can fire two submits before React has
    // re-rendered with the disabled button, so the ref is the real lock.
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.display_name}`);
      navigate(params.get("next") || (u.is_admin ? "/admin" : "/dashboard"));
    } catch (err) {
      setError(friendlyLoginError(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-10 sm:py-14">
      <div className="sm-card p-6 sm:p-7 bg-white">
        <span className="w-11 h-11 rounded-xl bg-[#1F6F78] text-white flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </span>
        <h1 className="mt-4 font-display text-2xl font-bold text-[#1F2A2E]">Welcome back</h1>
        <p className="mt-1.5 text-sm text-[#4B5A60]">
          Sign in to keep your recovery connections going.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" noValidate={false}>
          <div>
            <label
              htmlFor="login-email"
              className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]"
            >
              Email
            </label>
            <Input
              id="login-email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="mt-1.5 h-12 text-base bg-white border-[#E3DED4]"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label
              htmlFor="login-password"
              className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]"
            >
              Password
            </label>
            <Input
              id="login-password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              autoComplete="current-password"
              className="mt-1.5 h-12 text-base bg-white border-[#E3DED4]"
              placeholder="Your password"
            />
          </div>
          {error ? (
            <p
              data-testid="login-error"
              role="alert"
              aria-live="assertive"
              className="text-sm text-[#B42318] bg-[#FBE6E4] rounded-lg px-3 py-2"
            >
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            disabled={busy}
            aria-busy={busy}
            data-testid="login-submit-button"
            className="w-full h-12 text-base bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] disabled:opacity-80 disabled:cursor-not-allowed text-white touch-manipulation"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                Signing you in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        <p className="mt-5 text-sm text-[#4B5A60] text-center">
          New here?{" "}
          <Link
            to="/register"
            data-testid="login-to-register-link"
            className="inline-flex min-h-[44px] items-center font-semibold text-[#1F6F78] hover:underline"
          >
            Create a free account
          </Link>
        </p>
      </div>
    </div>
  );
}
